import React, { useEffect, useRef } from 'react';
import { analytics } from '@/utils/analytics';

interface ScrollTrackerProps {
  children: React.ReactNode;
  thresholds?: number[]; // Percentage thresholds to track (e.g., [25, 50, 75, 100])
}

export const ScrollTracker: React.FC<ScrollTrackerProps> = ({
  children,
  thresholds = [25, 50, 75, 100]
}) => {
  const trackedThresholds = useRef<Set<number>>(new Set());
  const startTime = useRef<number>(Date.now());

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercentage = Math.round((scrollTop / documentHeight) * 100);

      // Track each threshold only once
      thresholds.forEach(threshold => {
        if (scrollPercentage >= threshold && !trackedThresholds.current.has(threshold)) {
          trackedThresholds.current.add(threshold);
          
          analytics.trackScrollDepth(threshold);
          
          // Track time to reach threshold
          const timeToThreshold = Date.now() - startTime.current;
          analytics.trackEvent({
            action: 'scroll',
            category: 'engagement',
            label: `time_to_${threshold}%`,
            value: timeToThreshold,
            custom_parameters: {
              scroll_depth: threshold,
              time_to_reach_ms: timeToThreshold
            }
          });
        }
      });
    };

    // Throttle scroll events
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', throttledScroll);
    };
  }, [thresholds]);

  return <>{children}</>;
};

export default ScrollTracker;